import React from 'react';
import { useNavigate } from 'react-router-dom';

// Service Card Component
const ServiceCard = ({ icon, title, children }) => (
  <div className="bg-white p-6 rounded-lg shadow-md hover:shadow-xl transition-shadow duration-300">
    <div className="text-4xl mb-4 text-purple-600">{icon}</div>
    <h3 className="text-xl font-bold text-gray-800 mb-2">{title}</h3>
    <p className="text-gray-600">{children}</p>
  </div>
);

const HomePage = () => {
  const navigate = useNavigate();

  return (
    <div>
      {/* Hero Section with Gradient Background */}
      <div className="bg-gradient-to-br from-purple-700 via-purple-600 to-purple-800 text-white">
        <div className="max-w-7xl mx-auto py-24 px-4 sm:px-6 lg:px-8">
          <div className="text-center">
            {/* Main Tagline */}
            <h1 className="text-4xl sm:text-5xl lg:text-6xl font-extrabold mb-6 tracking-tight">
              Safe. Secure. Fast.
            </h1>
            
            {/* Subtitle */}
            <p className="text-lg sm:text-xl lg:text-2xl mb-10 max-w-3xl mx-auto leading-relaxed">
              Cargo delivery services to the UK, USA & ROTW. We handle Foodstuffs, Clothes, Artefacts, and Documents with expert Customs Clearance.
            </p>
            
            {/* Call-to-Action Buttons */}
            <div className="flex flex-col sm:flex-row gap-4 justify-center max-w-2xl mx-auto">
              <button
                onClick={() => navigate('/track')}
                className="group relative inline-flex items-center justify-center px-8 py-4 text-base font-semibold text-purple-600 bg-white rounded-lg hover:bg-gray-50 transition-all duration-300 shadow-lg hover:shadow-2xl hover:-translate-y-1"
              >
                <span className="mr-2">🔍</span>
                Track Shipment
              </button>
              
              <button
                onClick={() => navigate('/book-pickup')}
                className="group relative inline-flex items-center justify-center px-8 py-4 text-base font-semibold text-white bg-purple-900 border-2 border-white rounded-lg hover:bg-purple-950 transition-all duration-300 shadow-lg hover:shadow-2xl hover:-translate-y-1"
              >
                <span className="mr-2">📦</span>
                Book a Pickup
              </button>
            </div>
          </div>
        </div>
      </div>

      {/* Specialized Services Section */}
      <div className="py-20 bg-white">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-16">
            <h2 className="text-3xl sm:text-4xl font-extrabold text-gray-900 mb-4">
              Specialized Services
            </h2>
            <p className="text-lg text-gray-600">
              Expert handling for your unique cargo needs.
            </p>
          </div>
          
          <div className="grid grid-cols-1 md:grid-cols-2 gap-8">
            <ServiceCard icon="🍎" title="Foodstuffs & Perishables">
              Specialized handling of African foodstuffs for export, ensuring freshness and compliance with international standards.
            </ServiceCard>
            <ServiceCard icon="🏺" title="Artifacts & Textiles">
              Secure and careful handling of valuable artifacts and textiles for international shipping, preserving their cultural and monetary value.
            </ServiceCard>
          </div>
        </div>
      </div>

      {/* Core Services Section */}
      <div className="py-20 bg-gray-50">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-16">
            <h2 className="text-3xl sm:text-4xl font-extrabold text-gray-900 mb-4">
              Our Core Services
            </h2>
            <p className="text-lg text-gray-600">
              Comprehensive solutions for all your shipping needs.
            </p>
          </div>
          
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-8">
            <ServiceCard icon="🌍" title="Import & Export">
              Seamless import and export of goods with precision, managing all logistics from start to finish.
            </ServiceCard>
            <ServiceCard icon="🚢" title="Sea Freight">
              Cost-effective shipping for large volume goods, foodstuffs, and heavy machinery.
            </ServiceCard>
            <ServiceCard icon="✈️" title="Air Cargo">
              Expedited delivery to UK, USA & ROTW for time-sensitive documents and products.
            </ServiceCard>
            <ServiceCard icon="📋" title="Customs Clearance">
              Hassle-free documentation and clearance handling at all major ports and airports.
            </ServiceCard>
          </div>
        </div>
      </div>

      {/* Secondary CTA Section */}
      <div className="bg-purple-600 text-white py-16">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
          <h2 className="text-2xl sm:text-3xl lg:text-4xl font-bold mb-8">
            Ready to Ship Your Cargo?
          </h2>
          <button
            onClick={() => navigate('/book-pickup')}
            className="inline-flex items-center px-8 py-4 text-lg font-semibold bg-white text-purple-600 rounded-lg hover:bg-gray-100 transition-all duration-300 shadow-lg hover:shadow-xl hover:-translate-y-1"
          >
            <span className="mr-2">📋</span>
            Book Your Pickup Now
          </button>
        </div>
      </div>
    </div>
  );
};

export default HomePage;